import { CAPITAL_GLOBAL_PRODUCT_CODE } from '@/app/config/products.config';
import {
  CalculationCommercializationSection,
  CalculationInstallmentsTableSection,
  CalculationStipulantsListSection,
  CalculationSummaryCardSection,
} from '@/features/quotes/components/capital-global/steps/sections/step-calculation.sections';
import { useCapitalGlobalCoveragesCatalog } from '@/features/quotes/hooks/capital-global/use-capital-global-coverages-catalog.hook';
import { useCapitalGlobalBusinessSegmentLabel } from '@/features/quotes/hooks/capital-global/use-capital-global-business-segment-label.hook';
import { useDecideGlobalQuoteSubscription } from '@/features/quotes/hooks/capital-global/use-decide-global-quote-subscription.hook';
import { useCalculateCapitalGlobalAnalysis } from '@/features/quotes/hooks/capital-global/use-calculate-capital-global-analysis.hook';
import { useCapitalGlobalUnderwritingMessages } from '@/features/quotes/hooks/capital-global/use-capital-global-underwriting-messages.hook';
import { useProposalDetails } from '@/features/quotes/hooks/homeowner/use-proposal-details.hook';
import { mapProposalDetailsToCalculation } from '@/features/quotes/components/capital-global/steps/utils/map-proposal-details-to-calculation.util';
import { resolveStipulantSegmentCode } from '@/features/quotes/components/capital-global/steps/utils/resolve-stipulant-segment-code.util';
import { formatUnderwritingMessages } from '@/features/quotes/utils/format-underwriting-messages.util';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import { toaster } from '@/shared/ui/toaster.component';
import { Page, PageHeader } from '@/shared/ui/page';
import {
  Button,
  Field,
  FieldTextarea,
  RadioCard,
  Spinner,
  Tag,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, CircleCheck, CircleX, Home } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  /** Id da cotação Capital Global em análise (rota `/analyses/$policyId`). */
  policyId: string;
};

type AnalysisTabId = 'resume' | 'decision';

type DecisionValue = 'ACCEPT' | 'REJECT';

const TABS: { id: AnalysisTabId; label: string }[] = [
  { id: 'resume', label: 'Resumo da Cotação' },
  { id: 'decision', label: 'Decisão' },
];

const DECISION_OPTIONS = [
  {
    value: 'ACCEPT' as const,
    label: 'Aceitar Cotação',
    description: 'Executa o cálculo e libera a cotação',
    icon: <CircleCheck className='size-6' />,
  },
  {
    value: 'REJECT' as const,
    label: 'Recusar Cotação',
    description: 'Encerra a cotação com observação',
    icon: <CircleX className='size-6' />,
  },
];

const CARD_CLASS =
  'flex flex-col rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] sm:p-6';

const EMPTY_INSTALLMENT_ROWS = [
  'Mensal',
  'Bimestral',
  'Trimestral',
  'Quadrimestral',
  'Semestral',
  'Anual',
].map(periodicity => ({ periodicity, premiumValue: '—' }));

export function CapitalGlobalAnalysisPage({ policyId }: Props) {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<AnalysisTabId>('resume');
  const [decision, setDecision] = useState<DecisionValue | ''>('');
  const [observations, setObservations] = useState('');

  const { data: proposalDetails, isLoading: isLoadingDetails } =
    useProposalDetails(policyId);

  const {
    coverageCatalog,
    assistanceCatalog,
    assistanceFuneralOptions,
    capitalizationOptions,
    isLoading: isLoadingCatalog,
  } = useCapitalGlobalCoveragesCatalog();

  const segmentCode = resolveStipulantSegmentCode(proposalDetails);
  const { label: segmentLabel, isLoading: isLoadingSegmentLabel } =
    useCapitalGlobalBusinessSegmentLabel(segmentCode);

  const decideMutation = useDecideGlobalQuoteSubscription(policyId);

  const isUnderAnalysis =
    Number(proposalDetails?.statusPortal) === PortalStatus.QuoteUnderAnalysis;

  const { data: underwritingData } = useCapitalGlobalUnderwritingMessages(
    policyId,
    isUnderAnalysis && Boolean(policyId)
  );

  const underAnalysisMessages = useMemo(() => {
    if (!isUnderAnalysis) return [];
    return formatUnderwritingMessages(underwritingData?.messages ?? []);
  }, [isUnderAnalysis, underwritingData?.messages]);

  const calculateAnalysis = useCalculateCapitalGlobalAnalysis(policyId);
  const hasCalculatedRef = useRef(false);

  useEffect(() => {
    if (hasCalculatedRef.current) return;
    if (!isUnderAnalysis || !policyId) return;
    hasCalculatedRef.current = true;
    calculateAnalysis.mutate(undefined, {
      onError: error => {
        toaster.create({
          type: 'error',
          title: 'Não foi possível calcular o prêmio',
          description:
            error instanceof Error ? error.message : 'Erro inesperado',
        });
      },
    });
  }, [isUnderAnalysis, policyId, calculateAnalysis]);

  const effectiveProposalDetails = useMemo(() => {
    if (!proposalDetails) return proposalDetails;
    const freshDuePremium = calculateAnalysis.data?.DuePremium;
    if (freshDuePremium == null) return proposalDetails;
    return { ...proposalDetails, duePremium: freshDuePremium };
  }, [proposalDetails, calculateAnalysis.data]);

  const resume = useMemo(() => {
    if (!effectiveProposalDetails) return null;
    return mapProposalDetailsToCalculation(effectiveProposalDetails, {
      coverageCatalog,
      assistanceCatalog,
      assistanceFuneralOptions,
      capitalizationOptions,
      segmentLabel,
    });
  }, [
    effectiveProposalDetails,
    coverageCatalog,
    assistanceCatalog,
    assistanceFuneralOptions,
    capitalizationOptions,
    segmentLabel,
  ]);

  const isLoading =
    isLoadingDetails ||
    isLoadingCatalog ||
    isLoadingSegmentLabel;

  const displayResume = resume;

  const installments = displayResume?.installments ?? [];
  const installmentsToShow =
    installments.length > 0 ? installments : EMPTY_INSTALLMENT_ROWS;

  const quoteNumber =
    proposalDetails?.proposalNo ?? proposalDetails?.quotationNo ?? policyId;

  const breadcrumb = (
    <Page.Breadcrumb
      items={[
        {
          label: 'Pagina Inicial',
          href: '/dashboard',
          iconLeft: <Home className='h-4 w-4' />,
        },
        { label: 'Subscrição' },
        { label: 'Cotações em Análise', href: '/analyses' },
        { label: 'Análise Capital Global', active: true },
      ]}
    />
  );

  if (!isLoading && !proposalDetails) {
    return (
      <Page.Root>
        {breadcrumb}
        <Page.Content className='py-4'>
          <Text className='text-sm text-alba-gray-600'>
            Cotação Capital Global não encontrada.
          </Text>
        </Page.Content>
      </Page.Root>
    );
  }

  if (
    proposalDetails &&
    proposalDetails.productCode !== CAPITAL_GLOBAL_PRODUCT_CODE
  ) {
    return (
      <Page.Root>
        {breadcrumb}
        <Page.Content className='py-4'>
          <Text className='text-sm text-alba-gray-600'>
            Esta cotação não pertence ao produto Capital Global.
          </Text>
        </Page.Content>
      </Page.Root>
    );
  }

  if (
    proposalDetails &&
    Number(proposalDetails.statusPortal) !== PortalStatus.QuoteUnderAnalysis
  ) {
    return (
      <Page.Root>
        {breadcrumb}
        <Page.Content className='py-4'>
          <Text className='text-sm text-alba-gray-600'>
            A cotação não está em análise (status{' '}
            {proposalDetails.statusPortal ?? 'indefinido'}).
          </Text>
        </Page.Content>
      </Page.Root>
    );
  }

  const handleConfirm = () => {
    if (!decision) return;

    const trimmedObservations = observations.trim();
    if (decision === 'REJECT' && !trimmedObservations) {
      toaster.create({
        type: 'error',
        title: 'Informe a observação',
        description: 'Preencha o campo de observações antes de recusar.',
      });
      return;
    }

    decideMutation.mutate(
      {
        status: decision === 'ACCEPT' ? 'accepted' : 'rejected',
        description: trimmedObservations || undefined,
      },
      {
        onSuccess: result => {
          toaster.create({
            type: 'success',
            title:
              result.status === 'accepted'
                ? 'Cotação aprovada'
                : 'Cotação recusada',
            description:
              result.status === 'accepted'
                ? 'O cálculo foi executado e a cotação está disponível para o corretor.'
                : 'A cotação foi encerrada com a observação registrada.',
          });
          void navigate({ to: '/analyses' });
        },
        onError: error => {
          toaster.create({
            type: 'error',
            title: 'Não foi possível registrar a decisão',
            description:
              error instanceof Error ? error.message : 'Erro inesperado',
          });
        },
      },
    );
  };

  return (
    <Page.Root>
      {breadcrumb}

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>
              Análise Cotação Capital Global - Nº {quoteNumber}
            </PageHeader.Title>
            <PageHeader.Subtitle>
              Analise os dados da cotação e registre a decisão da subscrição
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
          <PageHeader.Actions>
            <Tag.Root variant='outline' colorScheme='yellow' size='lg'>
              <Tag.Label>Em Análise</Tag.Label>
            </Tag.Root>
          </PageHeader.Actions>
        </PageHeader.Main>
      </PageHeader.Root>

      <Page.Content className='flex flex-col gap-6 py-4'>
        {isLoading ? (
          <div className='flex justify-center py-16'>
            <Spinner size='lg' colorScheme='blue' />
          </div>
        ) : (
          <>
            <div
              role='tablist'
              className='flex w-full items-center gap-2 rounded-2xl bg-alba-gray-100 p-1.5'
            >
              {TABS.map(tab => {
                const isActive = tab.id === activeTab;
                return (
                  <button
                    key={tab.id}
                    type='button'
                    role='tab'
                    aria-selected={isActive}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex flex-1 flex-col items-center justify-center rounded-xl px-4 py-2.5 font-montserrat text-sm transition-colors ${
                      isActive
                        ? 'text-alba-green-500'
                        : 'text-alba-gray-500 hover:text-alba-black-500'
                    }`}
                  >
                    <span className={isActive ? 'font-bold' : ''}>
                      {tab.label}
                    </span>
                    <span
                      className={`mt-1 h-0.5 w-6 rounded-full ${
                        isActive ? 'bg-alba-green-500' : 'bg-transparent'
                      }`}
                    />
                  </button>
                );
              })}
            </div>

            {underAnalysisMessages.length > 0 && (
              <div className='flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4'>
                <AlertTriangle className='mt-0.5 size-5 shrink-0 text-amber-600' />
                <div className='flex flex-col gap-2'>
                  <Text className='text-sm font-bold text-alba-black-500'>
                    Motivos da subscrição automática
                  </Text>
                  <ul className='list-disc space-y-1 pl-5'>
                    {underAnalysisMessages.map((message, index) => (
                      <li key={index} className='text-sm text-alba-gray-700'>
                        {message}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'resume' ? (
              <>
                <div className={`${CARD_CLASS} gap-8`}>
                  <div className='flex justify-center'>
                    <CalculationInstallmentsTableSection items={installmentsToShow} />
                  </div>

                  <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
                    <CalculationSummaryCardSection
                      data={
                        displayResume?.insuranceDataCard ?? {
                          title: 'Dados do Seguro',
                          items: [],
                        }
                      }
                    />
                    <CalculationSummaryCardSection
                      data={
                        displayResume?.stipulantDataCard ?? {
                          title: 'Dados do Estipulante',
                          items: [],
                        }
                      }
                    />
                  </div>

                  <CalculationStipulantsListSection
                    entries={displayResume?.stipulantAccordions ?? []}
                  />

                  <CalculationCommercializationSection
                    data={
                      displayResume?.commercialization ?? {
                        brokeragePercent: '0,00 %',
                        agencingPercent: '0,00 %',
                      }
                    }
                  />
                </div>

                <div className='flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between'>
                  <Button
                    variant='ghost'
                    size='md'
                    onClick={() => void navigate({ to: '/analyses' })}
                  >
                    Sair
                  </Button>
                  <Button
                    variant='primary'
                    size='md'
                    onClick={() => setActiveTab('decision')}
                  >
                    Decisão
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className={`${CARD_CLASS} gap-6`}>
                  <Text className='text-base text-alba-black-500'>
                    Decisão final
                  </Text>

                  <RadioCard.Root
                    value={decision}
                    onValueChange={details =>
                      setDecision((details.value as DecisionValue) || '')
                    }
                    className='flex-row flex-wrap'
                  >
                    {DECISION_OPTIONS.map(option => (
                      <RadioCard.Item key={option.value} value={option.value}>
                        <RadioCard.ItemControl>
                          <RadioCard.ItemContent>
                            <RadioCard.ItemIcon className='mb-2'>
                              {option.icon}
                            </RadioCard.ItemIcon>
                            <RadioCard.ItemText>{option.label}</RadioCard.ItemText>
                            <RadioCard.ItemDescription className='mt-1'>
                              {option.description}
                            </RadioCard.ItemDescription>
                          </RadioCard.ItemContent>
                          <RadioCard.RadioButton />
                        </RadioCard.ItemControl>
                        <RadioCard.ItemHiddenInput />
                      </RadioCard.Item>
                    ))}
                  </RadioCard.Root>

                  <Field.Root>
                    <Field.Label>Observações</Field.Label>
                    <FieldTextarea
                      value={observations}
                      onChange={event => setObservations(event.target.value)}
                      placeholder='Digite as observações sobre a análise...'
                      rows={5}
                    />
                  </Field.Root>
                </div>

                <div className='flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between'>
                  <Button
                    variant='ghost'
                    size='md'
                    onClick={() => setActiveTab('resume')}
                    disabled={decideMutation.isPending}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant='primary'
                    size='md'
                    disabled={!decision || decideMutation.isPending}
                    loading={decideMutation.isPending}
                    onClick={handleConfirm}
                  >
                    Confirmar
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </Page.Content>
    </Page.Root>
  );
}
